import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { tap, catchError } from 'rxjs/operators';
import { throwError } from 'rxjs';
import { SerpaqDeliveriesActions } from './serpaqDeliveries.actions';
import { ISerpaqReturn, ISerpaqShipping } from '../../interfaces/deliveries.interface';
import { SerpaqDeliveriesService } from '../../services/serpaqDeliveries/serpaqDeliveries.service';

export interface SerpaqDeliveriesStateModel {
  shipments: ISerpaqShipping[];
  returns: ISerpaqReturn[];
  loading: boolean;
  refreshing: boolean;
  error: string | null;
}

@State<SerpaqDeliveriesStateModel>({
  name: 'serpaqDeliveries',
  defaults: {
    shipments: [],
    returns: [],
    loading: false,
    refreshing: false,
    error: null,
  },
})
@Injectable()
export class SerpaqDeliveriesState {
  constructor(private serpaqService: SerpaqDeliveriesService) {}

  @Selector()
  static getShipments(state: SerpaqDeliveriesStateModel): ISerpaqShipping[] {
    return state.shipments;
  }

  @Selector()
  static getReturns(state: SerpaqDeliveriesStateModel): ISerpaqReturn[] {
    return state.returns;
  }

  @Selector()
  static isLoading(state: SerpaqDeliveriesStateModel): boolean {
    return state.loading;
  }

  @Selector()
  static isRefreshing(state: SerpaqDeliveriesStateModel): boolean {
    return state.refreshing;
  }

  @Action(SerpaqDeliveriesActions.GetAll)
  getAll(ctx: StateContext<SerpaqDeliveriesStateModel>) {
    ctx.patchState({ loading: true, error: null });
    return this.serpaqService.getAll().pipe(
      tap({
        next: (response) => {
          ctx.patchState({
            shipments: response.data.shipments,
            returns: response.data.returns,
            loading: false,
          });
        },
        error: (error) => {
          ctx.patchState({ loading: false, error: error.message });
        },
      }),
      catchError((error) => {
        ctx.patchState({ loading: false, error: error.message });
        return throwError(() => error);
      }),
    );
  }

  @Action(SerpaqDeliveriesActions.SetAll)
  setAll(ctx: StateContext<SerpaqDeliveriesStateModel>, action: SerpaqDeliveriesActions.SetAll) {
    ctx.patchState({ shipments: action.shipments, returns: action.returns });
  }

  @Action(SerpaqDeliveriesActions.UploadExcel)
  uploadExcel(ctx: StateContext<SerpaqDeliveriesStateModel>, action: SerpaqDeliveriesActions.UploadExcel) {
    ctx.patchState({ loading: true, error: null });
    return this.serpaqService.uploadExcel(action.file).pipe(
      tap({
        next: () => {
          ctx.patchState({ loading: false });
          ctx.dispatch(new SerpaqDeliveriesActions.GetAll());
        },
        error: (error) => {
          ctx.patchState({ loading: false, error: error.message });
        },
      }),
      catchError((error) => {
        ctx.patchState({ loading: false, error: error.message });
        return throwError(() => error);
      }),
    );
  }

  @Action(SerpaqDeliveriesActions.LoadManual)
  loadManual(ctx: StateContext<SerpaqDeliveriesStateModel>, action: SerpaqDeliveriesActions.LoadManual) {
    ctx.patchState({ loading: true, error: null });
    return this.serpaqService.loadManual(action.trackings, action.target).pipe(
      tap({
        next: () => {
          ctx.patchState({ loading: false });
          ctx.dispatch(new SerpaqDeliveriesActions.GetAll());
        },
        error: (error) => {
          ctx.patchState({ loading: false, error: error.message });
        },
      }),
      catchError((error) => {
        ctx.patchState({ loading: false, error: error.message });
        return throwError(() => error);
      }),
    );
  }

  // Handler de SerpaqDeliveriesActions.SetType removido: la reclasificación
  // manual entre envíos y devoluciones ya no existe.

  @Action(SerpaqDeliveriesActions.RefreshStatuses)
  refreshStatuses(ctx: StateContext<SerpaqDeliveriesStateModel>) {
    ctx.patchState({ refreshing: true, error: null });
    return this.serpaqService.refreshStatuses().pipe(
      tap({
        next: () => {
          ctx.patchState({ refreshing: false });
          ctx.dispatch(new SerpaqDeliveriesActions.GetAll());
        },
        error: (error) => {
          ctx.patchState({ refreshing: false, error: error.message });
        },
      }),
      catchError((error) => {
        ctx.patchState({ refreshing: false, error: error.message });
        return throwError(() => error);
      }),
    );
  }
}
